import type {
  SupportChatTransport,
  SupportConversation,
  SupportMessage,
} from "./types";

/**
 * Minimal HTTP surface the widget needs, so the host can hand over whatever
 * it already uses — an axios instance with an interceptor, a fetch wrapper,
 * anything — without this package depending on a client library.
 */
export interface HttpAdapter {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, body?: unknown): Promise<T>;
}

/**
 * Build a transport from an HTTP adapter.
 *
 * Both frontends hit the same five routes under `/support`; only auth differs,
 * and that is entirely the adapter's business.
 */
export function createSupportTransport(
  http: HttpAdapter,
  basePath = "/support",
): SupportChatTransport {
  return {
    startConversation(message?: string) {
      return http.post<SupportConversation>(`${basePath}/conversations`, {
        message,
      });
    },

    listConversations() {
      return http.get<SupportConversation[]>(`${basePath}/conversations`);
    },

    getMessages(conversationUuid: string, afterId?: number) {
      // Only send the cursor when it is meaningful; `afterId=0` would be a
      // no-op the server has to parse anyway.
      const qs = afterId && afterId > 0 ? `?afterId=${afterId}` : "";
      return http.get<SupportMessage[]>(
        `${basePath}/conversations/${conversationUuid}/messages${qs}`,
      );
    },

    postMessage(
      conversationUuid: string,
      body: string,
      attachmentUuids?: string[],
    ) {
      return http.post<SupportMessage>(
        `${basePath}/conversations/${conversationUuid}/messages`,
        { body, ...(attachmentUuids?.length ? { attachmentUuids } : {}) },
      );
    },

    /**
     * presign -> PUT to S3 -> confirm.
     *
     * The PUT goes straight to S3 with `fetch`, NOT through the injected http
     * adapter: that adapter attaches our Authorization header, and sending it
     * to S3 alongside the presigned signature makes S3 reject the request.
     */
    async uploadFile(file: File, onProgress?: (pct: number) => void) {
      onProgress?.(0);
      const { uuid, presignedUrl } = await http.get<{
        uuid: string;
        presignedUrl: string;
      }>(`/uploads/presigned?fileName=${encodeURIComponent(file.name)}`);

      const put = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      onProgress?.(90);

      await http.post(`/uploads/${uuid}/complete`, {});
      onProgress?.(100);
      return uuid;
    },

    async getDownloadUrl(uploadUuid: string) {
      const res = await http.get<{ downloadUrl?: string; url?: string }>(
        `/uploads/${uploadUuid}/download`,
      );
      // Both shapes are in play across this codebase's endpoints.
      return res.downloadUrl ?? res.url ?? "";
    },

    escalate(conversationUuid: string, reason?: string) {
      return http.post<SupportConversation>(
        `${basePath}/conversations/${conversationUuid}/escalate`,
        { reason },
      );
    },
  };
}
